import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Send, MessageCircle, X, Globe2, Zap, Users, Eye, Brain, MapPin, ChevronDown, Home } from 'lucide-react';
import { useLanguage, LanguageToggle } from '@/contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

// Zener card symbols - Plus instead of cross
const SYMBOLS = [
  { id: 'circle', icon: '◯', name: 'Daire' },
  { id: 'cross', icon: '+', name: 'Artı' },
  { id: 'waves', icon: '≋', name: 'Dalga' },
  { id: 'square', icon: '□', name: 'Kare' },
  { id: 'star', icon: '☆', name: 'Yıldız' }
];

// Countries and cities data
const LOCATIONS = {
  'Türkiye': ['İstanbul', 'Ankara', 'İzmir', 'Bursa', 'Antalya', 'Adana', 'Konya', 'Gaziantep', 'Mersin', 'Diyarbakır'],
  'ABD': ['New York', 'Los Angeles', 'Chicago', 'Houston', 'Phoenix', 'San Francisco', 'Miami', 'Seattle', 'Boston', 'Denver'],
  'Almanya': ['Berlin', 'Münih', 'Hamburg', 'Frankfurt', 'Köln', 'Düsseldorf', 'Stuttgart', 'Leipzig', 'Dortmund', 'Essen'],
  'İngiltere': ['Londra', 'Manchester', 'Birmingham', 'Liverpool', 'Leeds', 'Bristol', 'Edinburgh', 'Glasgow', 'Cardiff', 'Belfast'],
  'Fransa': ['Paris', 'Marsilya', 'Lyon', 'Toulouse', 'Nice', 'Bordeaux', 'Lille', 'Strasbourg', 'Nantes', 'Montpellier'],
  'İtalya': ['Roma', 'Milano', 'Napoli', 'Torino', 'Floransa', 'Venedik', 'Bologna', 'Cenova', 'Palermo', 'Bari'],
  'İspanya': ['Madrid', 'Barselona', 'Valencia', 'Sevilla', 'Bilbao', 'Malaga', 'Zaragoza', 'Murcia', 'Palma', 'Granada'],
  'Japonya': ['Tokyo', 'Osaka', 'Kyoto', 'Yokohama', 'Nagoya', 'Sapporo', 'Fukuoka', 'Kobe', 'Hiroshima', 'Sendai'],
  'Brezilya': ['São Paulo', 'Rio de Janeiro', 'Brasília', 'Salvador', 'Fortaleza', 'Belo Horizonte', 'Manaus', 'Curitiba', 'Recife', 'Porto Alegre'],
  'Hindistan': ['Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Chennai', 'Kolkata', 'Ahmedabad', 'Pune', 'Jaipur', 'Lucknow'],
  'Rusya': ['Moskova', 'St. Petersburg', 'Novosibirsk', 'Yekaterinburg', 'Kazan', 'Nizhny Novgorod', 'Samara', 'Omsk', 'Rostov', 'Ufa'],
  'Avustralya': ['Sydney', 'Melbourne', 'Brisbane', 'Perth', 'Adelaide', 'Gold Coast', 'Canberra', 'Newcastle', 'Hobart', 'Darwin'],
  'Kanada': ['Toronto', 'Montreal', 'Vancouver', 'Calgary', 'Edmonton', 'Ottawa', 'Winnipeg', 'Quebec City', 'Halifax', 'Victoria'],
  'Güney Kore': ['Seul', 'Busan', 'Incheon', 'Daegu', 'Daejeon', 'Gwangju', 'Ulsan', 'Suwon', 'Changwon', 'Goyang'],
  'Meksika': ['Mexico City', 'Guadalajara', 'Monterrey', 'Puebla', 'Tijuana', 'León', 'Juárez', 'Cancún', 'Mérida', 'Querétaro']
};

// Country coordinates for globe positioning
const COUNTRY_COORDS = {
  'Türkiye': { lat: 39, lon: 35 },
  'ABD': { lat: 37, lon: -95 },
  'Almanya': { lat: 51, lon: 10 },
  'İngiltere': { lat: 52, lon: -1 },
  'Fransa': { lat: 46, lon: 2 },
  'İtalya': { lat: 42, lon: 12 },
  'İspanya': { lat: 40, lon: -4 },
  'Japonya': { lat: 36, lon: 138 },
  'Brezilya': { lat: -14, lon: -51 },
  'Hindistan': { lat: 21, lon: 78 },
  'Rusya': { lat: 60, lon: 100 },
  'Avustralya': { lat: -25, lon: 134 },
  'Kanada': { lat: 56, lon: -106 },
  'Güney Kore': { lat: 36, lon: 128 },
  'Meksika': { lat: 23, lon: -102 }
};

// Enhanced 3D Globe Component
const Globe3D = ({ userLocation, partnerLocation, onAnimationComplete }) => {
  const [phase, setPhase] = useState('intro');
  const [rotation, setRotation] = useState(0);
  
  useEffect(() => {
    // Continuous rotation
    const rotationInterval = setInterval(() => {
      setRotation(prev => (prev + 0.5) % 360);
    }, 50);
    
    const timer1 = setTimeout(() => setPhase('connecting'), 2000);
    const timer2 = setTimeout(() => setPhase('complete'), 5000);
    const timer3 = setTimeout(() => onAnimationComplete(), 7000);
    
    return () => {
      clearInterval(rotationInterval);
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [onAnimationComplete]);
  
  // Calculate position on globe from coordinates
  const getGlobePosition = (lat, lon) => {
    const latRad = (lat * Math.PI) / 180;
    const lonRad = ((lon + rotation) * Math.PI) / 180;
    const x = 50 + 40 * Math.cos(latRad) * Math.sin(lonRad);
    const y = 50 - 40 * Math.sin(latRad);
    const z = Math.cos(latRad) * Math.cos(lonRad);
    return { x, y, visible: z > -0.2 };
  };
  
  const userCoords = COUNTRY_COORDS[userLocation?.country] || { lat: 39, lon: 35 };
  const partnerCoords = COUNTRY_COORDS[partnerLocation?.country] || { lat: 51, lon: 10 };
  
  const userPos = getGlobePosition(userCoords.lat, userCoords.lon);
  const partnerPos = getGlobePosition(partnerCoords.lat, partnerCoords.lon);
  
  return (
    <div className="fixed inset-0 z-50 bg-slate-950 flex items-center justify-center overflow-hidden">
      {/* Stars background - more stars */}
      <div className="absolute inset-0">
        {[...Array(150)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-white"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: Math.random() > 0.9 ? '3px' : Math.random() > 0.7 ? '2px' : '1px',
              height: Math.random() > 0.9 ? '3px' : Math.random() > 0.7 ? '2px' : '1px',
            }}
            animate={{ opacity: [0.1, 0.8, 0.1] }}
            transition={{ duration: 1 + Math.random() * 3, repeat: Infinity, delay: Math.random() * 2 }}
          />
        ))}
      </div>
      
      {/* Nebula effects */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-violet-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-cyan-500/20 rounded-full blur-3xl" />
      </div>
      
      {/* Large Detailed Globe */}
      <div className="relative w-[500px] h-[500px] md:w-[600px] md:h-[600px]">
        {/* Globe outer glow */}
        <div 
          className="absolute inset-0 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(6, 182, 212, 0.15) 0%, transparent 70%)',
            transform: 'scale(1.3)'
          }}
        />
        
        {/* Main globe sphere */}
        <motion.div
          className="absolute inset-0 rounded-full overflow-hidden"
          style={{
            background: `
              radial-gradient(circle at 35% 35%, #1e4a6e 0%, #0d2847 30%, #061428 60%, #020810 100%)
            `,
            boxShadow: `
              0 0 100px rgba(6, 182, 212, 0.4),
              0 0 200px rgba(6, 182, 212, 0.2),
              inset -30px -30px 80px rgba(0,0,0,0.8),
              inset 20px 20px 60px rgba(6, 182, 212, 0.15)
            `
          }}
        >
          {/* Detailed grid lines */}
          <svg 
            className="absolute inset-0 w-full h-full" 
            viewBox="0 0 100 100"
            style={{ transform: `rotateY(${rotation}deg)` }}
          >
            {/* Latitude lines (horizontal) */}
            {[-60, -40, -20, 0, 20, 40, 60].map((lat, i) => {
              const y = 50 - lat * 0.7;
              const width = Math.cos((lat * Math.PI) / 180) * 45;
              return (
                <ellipse
                  key={`lat-${i}`}
                  cx="50"
                  cy={y}
                  rx={width}
                  ry={width * 0.2}
                  fill="none"
                  stroke="rgba(6, 182, 212, 0.2)"
                  strokeWidth="0.3"
                />
              );
            })}
            
            {/* Longitude lines (vertical) */}
            {[0, 30, 60, 90, 120, 150].map((lon, i) => (
              <ellipse
                key={`lon-${i}`}
                cx="50"
                cy="50"
                rx={8 + (i % 2) * 5}
                ry="45"
                fill="none"
                stroke="rgba(6, 182, 212, 0.15)"
                strokeWidth="0.3"
                transform={`rotate(${lon + rotation * 0.1}, 50, 50)`}
              />
            ))}
          </svg>
          
          {/* Continents SVG */}
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100">
            {/* North America */}
            <path
              d="M 15 25 Q 20 20 28 22 L 30 28 Q 35 30 32 38 L 25 42 Q 20 40 18 35 Z"
              fill="rgba(139, 92, 246, 0.3)"
              stroke="rgba(139, 92, 246, 0.4)"
              strokeWidth="0.3"
              style={{ transform: `translateX(${Math.sin(rotation * 0.02) * 2}px)` }}
            />
            {/* South America */}
            <path
              d="M 28 50 Q 32 48 35 52 L 33 62 Q 30 68 27 65 L 25 55 Z"
              fill="rgba(139, 92, 246, 0.3)"
              stroke="rgba(139, 92, 246, 0.4)"
              strokeWidth="0.3"
            />
            {/* Europe */}
            <path
              d="M 45 22 Q 52 20 55 24 L 54 30 Q 50 32 46 28 Z"
              fill="rgba(34, 211, 238, 0.3)"
              stroke="rgba(34, 211, 238, 0.4)"
              strokeWidth="0.3"
            />
            {/* Africa */}
            <path
              d="M 45 35 Q 55 33 58 42 L 55 55 Q 50 60 45 55 L 43 45 Z"
              fill="rgba(139, 92, 246, 0.3)"
              stroke="rgba(139, 92, 246, 0.4)"
              strokeWidth="0.3"
            />
            {/* Asia */}
            <path
              d="M 55 20 Q 75 18 80 28 L 78 40 Q 70 45 60 40 L 58 30 Z"
              fill="rgba(34, 211, 238, 0.3)"
              stroke="rgba(34, 211, 238, 0.4)"
              strokeWidth="0.3"
            />
            {/* Australia */}
            <path
              d="M 75 55 Q 82 52 85 58 L 82 65 Q 78 68 75 62 Z"
              fill="rgba(139, 92, 246, 0.3)"
              stroke="rgba(139, 92, 246, 0.4)"
              strokeWidth="0.3"
            />
          </svg>
          
          {/* Atmospheric glow */}
          <div 
            className="absolute inset-0 rounded-full"
            style={{
              background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.1) 0%, transparent 50%)'
            }}
          />
        </motion.div>
        
        {/* User location point */}
        {userPos.visible && (
          <motion.div
            className="absolute"
            style={{
              left: `${userPos.x}%`,
              top: `${userPos.y}%`,
              transform: 'translate(-50%, -50%)'
            }}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            <div className="relative">
              <div 
                className="w-5 h-5 rounded-full bg-cyan-400"
                style={{ boxShadow: '0 0 20px rgba(6, 182, 212, 0.8), 0 0 40px rgba(6, 182, 212, 0.4)' }}
              />
              <motion.div
                className="absolute inset-0 rounded-full border-2 border-cyan-400"
                animate={{ scale: [1, 2, 1], opacity: [1, 0, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </div>
          </motion.div>
        )}
        
        {/* Partner location point */}
        {partnerPos.visible && (
          <motion.div
            className="absolute"
            style={{
              left: `${partnerPos.x}%`,
              top: `${partnerPos.y}%`,
              transform: 'translate(-50%, -50%)'
            }}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 1, duration: 0.8 }}
          >
            <div className="relative">
              <div 
                className="w-5 h-5 rounded-full bg-violet-400"
                style={{ boxShadow: '0 0 20px rgba(139, 92, 246, 0.8), 0 0 40px rgba(139, 92, 246, 0.4)' }}
              />
              <motion.div
                className="absolute inset-0 rounded-full border-2 border-violet-400"
                animate={{ scale: [1, 2, 1], opacity: [1, 0, 1] }}
                transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
              />
            </div>
          </motion.div>
        )}
        
        {/* Connection arc */}
        <AnimatePresence>
          {phase !== 'intro' && userPos.visible && partnerPos.visible && (
            <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100">
              <defs>
                <linearGradient id="arcGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#22d3ee" />
                  <stop offset="50%" stopColor="#a78bfa" />
                  <stop offset="100%" stopColor="#8b5cf6" />
                </linearGradient>
                <filter id="arcGlow">
                  <feGaussianBlur stdDeviation="1" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>
              <motion.path
                d={`M ${userPos.x} ${userPos.y} Q 50 ${Math.min(userPos.y, partnerPos.y) - 20} ${partnerPos.x} ${partnerPos.y}`}
                stroke="url(#arcGrad)"
                strokeWidth="0.8"
                fill="none"
                strokeLinecap="round"
                filter="url(#arcGlow)"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ duration: 2 }}
              />
              
              {/* Energy particles along arc */}
              {[0, 1, 2].map((i) => (
                <motion.circle
                  key={i}
                  r="1"
                  fill={i % 2 === 0 ? '#22d3ee' : '#a78bfa'}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0, 1, 0] }}
                  transition={{ duration: 2, repeat: Infinity, delay: i * 0.7 }}
                >
                  <animateMotion
                    dur="2s"
                    repeatCount="indefinite"
                    path={`M ${userPos.x} ${userPos.y} Q 50 ${Math.min(userPos.y, partnerPos.y) - 20} ${partnerPos.x} ${partnerPos.y}`}
                  />
                </motion.circle>
              ))}
            </svg>
          )}
        </AnimatePresence>
        
        {/* Meeting point flash */}
        <AnimatePresence>
          {phase === 'complete' && (
            <motion.div
              className="absolute rounded-full"
              style={{
                left: '50%',
                top: '30%',
                transform: 'translate(-50%, -50%)',
                width: '60px',
                height: '60px',
                background: 'radial-gradient(circle, rgba(255,255,255,0.9) 0%, rgba(139,92,246,0.5) 40%, transparent 70%)'
              }}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: [0, 4, 3], opacity: [0, 1, 0.5] }}
              transition={{ duration: 1.5 }}
            />
          )}
        </AnimatePresence>
      </div>
      
      {/* Location labels */}
      <motion.div
        className="absolute bottom-24 left-1/2 -translate-x-1/2 text-center space-y-3"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5 }}
      >
        <div className="flex items-center justify-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-cyan-400" style={{ boxShadow: '0 0 10px rgba(6, 182, 212, 0.6)' }} />
            <span className="text-cyan-400 text-sm font-medium">
              {userLocation?.city}, {userLocation?.country}
            </span>
          </div>
        </div>
        
        <div className="flex items-center justify-center gap-3 text-slate-400">
          <motion.div
            className="w-12 h-px bg-gradient-to-r from-cyan-400 to-transparent"
            animate={{ scaleX: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
          <Zap className="w-5 h-5 text-violet-400" />
          <motion.div
            className="w-12 h-px bg-gradient-to-l from-violet-400 to-transparent"
            animate={{ scaleX: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </div>
        
        <div className="flex items-center justify-center gap-2">
          <div className="w-3 h-3 rounded-full bg-violet-400" style={{ boxShadow: '0 0 10px rgba(139, 92, 246, 0.6)' }} />
          <span className="text-violet-400 text-sm font-medium">
            {partnerLocation?.city}, {partnerLocation?.country}
          </span>
        </div>
      </motion.div>
      
      {/* Title */}
      <motion.div
        className="absolute top-16 left-1/2 -translate-x-1/2 text-center"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <h2 className="text-3xl font-heading text-slate-100 mb-2">
          {phase === 'intro' && 'Bağlantı Kuruluyor...'}
          {phase === 'connecting' && 'Zihinler Buluşuyor...'}
          {phase === 'complete' && 'Bağlantı Kuruldu!'}
        </h2>
        <p className="text-slate-400 text-sm">
          İki zihin, sonsuz uzayda buluşuyor
        </p>
      </motion.div>
    </div>
  );
};

// Location Selector Component
const LocationSelector = ({ onSelect }) => {
  const { t } = useLanguage();
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [showCountryList, setShowCountryList] = useState(false);
  const [showCityList, setShowCityList] = useState(false);
  
  const countries = Object.keys(LOCATIONS);
  const cities = selectedCountry ? LOCATIONS[selectedCountry] : [];
  
  // Auto-select when city is chosen
  useEffect(() => {
    if (selectedCountry && selectedCity) {
      onSelect({ country: selectedCountry, city: selectedCity });
    }
  }, [selectedCountry, selectedCity, onSelect]);
  
  return (
    <div className="space-y-6">
      {/* Country Selector */}
      <div className="space-y-2">
        <label className="text-xs text-slate-400 uppercase tracking-wider">{t('country')}</label>
        <div className="relative">
          <button
            onClick={() => { setShowCountryList(!showCountryList); setShowCityList(false); }}
            className="w-full glass-panel px-4 py-3 text-left flex items-center justify-between"
          >
            <span className={selectedCountry ? 'text-white' : 'text-slate-500'}>
              {selectedCountry || t('selectCountry')}
            </span>
            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showCountryList ? 'rotate-180' : ''}`} />
          </button>
          
          <AnimatePresence>
            {showCountryList && (
              <motion.div
                className="absolute z-50 w-full mt-2 glass-panel max-h-60 overflow-y-auto"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                {countries.map((country) => (
                  <button
                    key={country}
                    onClick={() => {
                      setSelectedCountry(country);
                      setSelectedCity('');
                      setShowCountryList(false);
                    }}
                    className={`w-full px-4 py-2 text-left hover:bg-violet-500/20 transition-colors ${
                      selectedCountry === country ? 'bg-violet-500/20 text-violet-400' : 'text-slate-300'
                    }`}
                  >
                    {country}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      
      {/* City Selector */}
      <div className="space-y-2">
        <label className="text-xs text-slate-400 uppercase tracking-wider">{t('city')}</label>
        <div className="relative">
          <button
            onClick={() => { if (selectedCountry) { setShowCityList(!showCityList); setShowCountryList(false); }}}
            disabled={!selectedCountry}
            className={`w-full glass-panel px-4 py-3 text-left flex items-center justify-between ${
              !selectedCountry ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <span className={selectedCity ? 'text-white' : 'text-slate-500'}>
              {selectedCity || t('selectCity')}
            </span>
            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showCityList ? 'rotate-180' : ''}`} />
          </button>
          
          <AnimatePresence>
            {showCityList && (
              <motion.div
                className="absolute z-50 w-full mt-2 glass-panel max-h-60 overflow-y-auto"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                {cities.map((city) => (
                  <button
                    key={city}
                    onClick={() => {
                      setSelectedCity(city);
                      setShowCityList(false);
                    }}
                    className={`w-full px-4 py-2 text-left hover:bg-cyan-500/20 transition-colors ${
                      selectedCity === city ? 'bg-cyan-500/20 text-cyan-400' : 'text-slate-300'
                    }`}
                  >
                    {city}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

// Nickname Entry Screen with Location
const NicknameEntry = ({ onSubmit, onBack }) => {
  const { t } = useLanguage();
  const [nickname, setNickname] = useState('');
  const [location, setLocation] = useState(null);
  const [step, setStep] = useState('nickname'); // nickname, location
  
  const handleNicknameNext = () => {
    if (nickname.trim()) {
      setStep('location');
    }
  };
  
  const handleLocationSelect = useCallback((loc) => {
    setLocation(loc);
  }, []);
  
  const handleSubmit = () => {
    if (nickname && location) {
      onSubmit(nickname, location);
    }
  };
  
  return (
    <motion.div
      className="min-h-screen flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <button
        onClick={onBack}
        className="fixed top-6 left-6 z-40 glass-panel p-3 text-slate-400 hover:text-white transition-all"
      >
        <ArrowLeft className="w-5 h-5" />
      </button>
      
      {/* Language Toggle */}
      <div className="fixed top-6 right-6 z-40">
        <LanguageToggle />
      </div>
      
      <div className="max-w-md w-full space-y-8 text-center">
        <div className="space-y-4">
          <motion.div
            className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-cyan-500/20 to-violet-500/20 flex items-center justify-center border border-violet-500/30"
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            {step === 'nickname' ? (
              <Brain className="w-10 h-10 text-violet-400" />
            ) : (
              <MapPin className="w-10 h-10 text-cyan-400" />
            )}
          </motion.div>
          
          <h1 className="text-3xl font-heading text-slate-100">{t('telepathyTitle')}</h1>
          <p className="text-slate-400 text-sm">
            {step === 'nickname' 
              ? t('telepathySubtitle')
              : t('selectLocation')
            }
          </p>
        </div>
        
        {step === 'nickname' ? (
          <div className="space-y-6">
            <input
              type="text"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder={t('nickname')}
              className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-lg text-center text-white placeholder:text-white/30 focus:border-violet-500 focus:outline-none transition-colors font-heading"
              maxLength={20}
              autoFocus
            />
            
            <button
              onClick={handleNicknameNext}
              disabled={!nickname.trim()}
              className="w-full rounded-full bg-gradient-to-r from-violet-500/20 to-cyan-500/20 px-8 py-4 text-sm font-medium text-white transition-all hover:from-violet-500/30 hover:to-cyan-500/30 border border-violet-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('continueBtn')}
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            <LocationSelector 
              onSelect={handleLocationSelect}
              onBack={() => setStep('nickname')}
            />
            
            {location && (
              <div className="glass-panel p-3 flex items-center justify-center gap-2">
                <MapPin className="w-4 h-4 text-cyan-400" />
                <span className="text-cyan-400 text-sm">{location.city}, {location.country}</span>
              </div>
            )}
            
            <div className="flex gap-3">
              <button
                onClick={() => setStep('nickname')}
                className="flex-1 rounded-full bg-slate-800/50 px-6 py-4 text-sm font-medium text-slate-300 border border-slate-700/50"
              >
                {t('back')}
              </button>
              <button
                onClick={handleSubmit}
                disabled={!location}
                className="flex-1 rounded-full bg-gradient-to-r from-violet-500/20 to-cyan-500/20 px-6 py-4 text-sm font-medium text-white transition-all hover:from-violet-500/30 hover:to-cyan-500/30 border border-violet-500/30 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Users className="w-4 h-4" />
                {t('findPartner')}
              </button>
            </div>
          </div>
        )}
        
        <div className="text-xs text-slate-500 space-y-1">
          <p>• {t('randomMatch')}</p>
          <p>• {t('noRegistration')}</p>
          <p>• {t('tenRounds')}</p>
        </div>
      </div>
    </motion.div>
  );
};

// Waiting Screen
const WaitingScreen = ({ location, onCancel }) => {
  const { t } = useLanguage();
  const [rotation, setRotation] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setRotation(prev => (prev + 1) % 360);
    }, 50);
    return () => clearInterval(interval);
  }, []);
  
  return (
    <motion.div
      className="min-h-screen flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <div className="max-w-md w-full space-y-8 text-center">
        {/* Rotating globe mini */}
        <div className="relative w-40 h-40 mx-auto">
          <motion.div
            className="absolute inset-0 rounded-full"
            style={{
              background: 'radial-gradient(circle at 30% 30%, #1e4a6e 0%, #0d2847 40%, #020810 100%)',
              boxShadow: '0 0 40px rgba(6, 182, 212, 0.3)',
              transform: `rotateY(${rotation}deg)`
            }}
          >
            <svg className="absolute inset-0 w-full h-full opacity-40" viewBox="0 0 100 100">
              {[0, 30, 60, 90, 120, 150].map((lon, i) => (
                <ellipse
                  key={i}
                  cx="50"
                  cy="50"
                  rx="10"
                  ry="45"
                  fill="none"
                  stroke="rgba(6, 182, 212, 0.3)"
                  strokeWidth="0.5"
                  transform={`rotate(${lon}, 50, 50)`}
                />
              ))}
            </svg>
          </motion.div>
          
          {/* Orbiting dot */}
          <motion.div
            className="absolute w-3 h-3 rounded-full bg-violet-400"
            style={{
              boxShadow: '0 0 15px rgba(139, 92, 246, 0.8)',
              left: '50%',
              top: '50%',
              transform: `rotate(${rotation * 2}deg) translateX(60px) translateY(-50%)`
            }}
          />
        </div>
        
        <div className="space-y-2">
          <h2 className="text-2xl font-heading text-slate-100">{t('searchingPartner')}</h2>
          <p className="text-slate-400 text-sm">
            {t('pleaseWait')}
          </p>
          {location && (
            <div className="flex items-center justify-center gap-2 text-cyan-400/80 text-sm mt-4">
              <MapPin className="w-4 h-4" />
              <span>{location.city}, {location.country}</span>
            </div>
          )}
        </div>
        
        <motion.div
          className="flex justify-center gap-1"
        >
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="w-2 h-2 rounded-full bg-violet-400"
              animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
            />
          ))}
        </motion.div>
        
        <button
          onClick={onCancel}
          className="text-slate-500 hover:text-slate-300 text-sm transition-colors"
        >
          {t('cancel')}
        </button>
      </div>
    </motion.div>
  );
};

// Game Screen - Sender View
const SenderView = ({ symbol, onSendThought, partnerNickname }) => {
  const [sent, setSent] = useState(false);
  
  const symbolData = SYMBOLS.find(s => s.id === symbol);
  
  const handleSend = () => {
    setSent(true);
    onSendThought();
  };
  
  return (
    <div className="space-y-8 text-center">
      <div className="space-y-2">
        <p className="text-xs tracking-widest uppercase text-cyan-400">Gönderici</p>
        <h2 className="text-xl font-heading text-slate-100">Bu sembole odaklanın</h2>
        <p className="text-slate-400 text-sm">
          {partnerNickname} tahmin etmeye çalışacak
        </p>
      </div>
      
      <motion.div
        className="w-40 h-40 mx-auto rounded-2xl bg-gradient-to-br from-violet-500/20 to-cyan-500/20 border border-violet-500/30 flex items-center justify-center"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring' }}
      >
        <span className="text-7xl text-violet-300">{symbolData?.icon}</span>
      </motion.div>
      
      <p className="text-slate-300 font-medium">{symbolData?.name}</p>
      
      <button
        onClick={handleSend}
        disabled={sent}
        className={`rounded-full px-8 py-4 text-sm font-medium transition-all flex items-center justify-center gap-2 mx-auto ${
          sent 
            ? 'bg-green-500/20 text-green-400 border border-green-500/30'
            : 'bg-gradient-to-r from-violet-500/20 to-cyan-500/20 text-white border border-violet-500/30 hover:from-violet-500/30 hover:to-cyan-500/30'
        }`}
      >
        {sent ? (
          <>
            <Eye className="w-4 h-4" />
            Düşünce Gönderildi
          </>
        ) : (
          <>
            <Send className="w-4 h-4" />
            Düşünceyi Gönder
          </>
        )}
      </button>
      
      {sent && (
        <motion.p
          className="text-slate-500 text-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          Partner tahmin ediyor...
        </motion.p>
      )}
    </div>
  );
};

// Game Screen - Receiver View
const ReceiverView = ({ onGuess, canGuess, partnerNickname }) => {
  return (
    <div className="space-y-8 text-center">
      <div className="space-y-2">
        <p className="text-xs tracking-widest uppercase text-violet-400">Alıcı</p>
        <h2 className="text-xl font-heading text-slate-100">
          {canGuess ? 'Sezginizi kullanın' : 'Bekleyin...'}
        </h2>
        <p className="text-slate-400 text-sm">
          {canGuess 
            ? `${partnerNickname} düşüncesini gönderdi` 
            : `${partnerNickname} odaklanıyor`
          }
        </p>
      </div>
      
      <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto">
        {SYMBOLS.map((symbol, index) => (
          <motion.button
            key={symbol.id}
            onClick={() => canGuess && onGuess(symbol.id)}
            disabled={!canGuess}
            className={`aspect-square rounded-xl border flex flex-col items-center justify-center gap-1 transition-all ${
              canGuess
                ? 'bg-slate-800/50 border-violet-500/30 hover:bg-violet-500/20 hover:border-violet-500/50'
                : 'bg-slate-900/50 border-slate-700/30 opacity-50'
            }`}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: index * 0.1 }}
            whileHover={canGuess ? { scale: 1.05 } : {}}
            whileTap={canGuess ? { scale: 0.95 } : {}}
          >
            <span className="text-3xl text-slate-300">{symbol.icon}</span>
            <span className="text-xs text-slate-500">{symbol.name}</span>
          </motion.button>
        ))}
      </div>
      
      {!canGuess && (
        <motion.div className="flex justify-center gap-1">
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="w-2 h-2 rounded-full bg-violet-400"
              animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
            />
          ))}
        </motion.div>
      )}
    </div>
  );
};

// Round Result Component
const RoundResult = ({ result }) => {
  return (
    <motion.div
      className="fixed inset-0 z-50 bg-slate-950/90 flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <motion.div
        className="glass-panel p-8 max-w-sm w-full text-center space-y-6"
        initial={{ scale: 0.8 }}
        animate={{ scale: 1 }}
      >
        <motion.div
          className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center ${
            result.correct 
              ? 'bg-green-500/20 border-2 border-green-500/50' 
              : 'bg-red-500/20 border-2 border-red-500/50'
          }`}
          initial={{ scale: 0 }}
          animate={{ scale: [0, 1.2, 1] }}
        >
          <span className="text-4xl">
            {result.correct ? '✓' : '✗'}
          </span>
        </motion.div>
        
        <div>
          <h3 className={`text-xl font-heading ${result.correct ? 'text-green-400' : 'text-red-400'}`}>
            {result.correct ? 'Doğru!' : 'Yanlış'}
          </h3>
          <p className="text-slate-400 text-sm mt-2">
            Gönderilen: <span className="text-violet-400">{SYMBOLS.find(s => s.id === result.actual_symbol)?.name}</span>
          </p>
          {!result.correct && (
            <p className="text-slate-500 text-xs">
              Tahmin: {SYMBOLS.find(s => s.id === result.guessed_symbol)?.name}
            </p>
          )}
        </div>
        
        <div className="text-sm text-slate-400">
          Skor: <span className="text-cyan-400">{result.total_correct}</span> / {result.total_rounds_played}
        </div>
      </motion.div>
    </motion.div>
  );
};

// Mini Chat Component
const MiniChat = ({ messages, onSend, isOpen, onToggle }) => {
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);
  
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (input.trim()) {
      onSend(input.trim());
      setInput('');
    }
  };
  
  return (
    <>
      <button
        onClick={onToggle}
        className="fixed bottom-6 right-6 z-40 glass-panel p-3 text-slate-400 hover:text-white transition-all"
      >
        <MessageCircle className="w-5 h-5" />
        {messages.length > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-violet-500 rounded-full text-xs text-white flex items-center justify-center">
            {messages.length}
          </span>
        )}
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed bottom-20 right-6 z-40 w-80 glass-panel overflow-hidden"
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
          >
            <div className="p-3 border-b border-white/10 flex items-center justify-between">
              <span className="text-sm text-slate-300">Oturum Sohbeti</span>
              <button onClick={onToggle} className="text-slate-500 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <div className="h-48 overflow-y-auto p-3 space-y-2">
              {messages.length === 0 ? (
                <p className="text-slate-500 text-xs text-center">Henüz mesaj yok</p>
              ) : (
                messages.map((msg, i) => (
                  <div key={msg.timestamp || `msg-${i}`} className="text-sm">
                    <span className="text-violet-400">{msg.nickname}: </span>
                    <span className="text-slate-300">{msg.message}</span>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>
            
            <form onSubmit={handleSubmit} className="p-2 border-t border-white/10 flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Mesaj..."
                className="flex-1 bg-slate-800/50 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-violet-500/50"
                maxLength={200}
              />
              <button
                type="submit"
                className="p-2 bg-violet-500/20 rounded-lg text-violet-400 hover:bg-violet-500/30 transition-colors"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Game Over Summary
const GameSummary = ({ summary, onPlayAgain, onExit }) => {
  const getStrengthColor = (strength) => {
    switch (strength) {
      case 'Güçlü': return 'text-green-400';
      case 'Orta': return 'text-yellow-400';
      default: return 'text-red-400';
    }
  };
  
  return (
    <motion.div
      className="min-h-screen flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <div className="glass-panel max-w-md w-full p-8 space-y-8 text-center">
        <div className="space-y-2">
          <motion.div
            className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-violet-500/20 to-cyan-500/20 flex items-center justify-center border border-violet-500/30"
            initial={{ scale: 0 }}
            animate={{ scale: [0, 1.2, 1] }}
          >
            <Brain className="w-10 h-10 text-violet-400" />
          </motion.div>
          <h2 className="text-2xl font-heading text-slate-100">Oturum Tamamlandı</h2>
        </div>
        
        <div className="space-y-4">
          <div className="glass-panel p-4 space-y-2">
            <p className="text-4xl font-bold text-cyan-400">{summary.total_correct} / {summary.total_rounds}</p>
            <p className="text-slate-400 text-sm">Doğru Tahmin</p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="glass-panel p-3">
              <p className="text-2xl font-bold text-violet-400">{summary.accuracy.toFixed(0)}%</p>
              <p className="text-slate-500 text-xs">Doğruluk</p>
            </div>
            <div className="glass-panel p-3">
              <p className={`text-xl font-bold ${getStrengthColor(summary.connection_strength)}`}>
                {summary.connection_strength}
              </p>
              <p className="text-slate-500 text-xs">Bağlantı Gücü</p>
            </div>
          </div>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={onPlayAgain}
            className="flex-1 rounded-full bg-gradient-to-r from-violet-500/20 to-cyan-500/20 px-6 py-3 text-sm font-medium text-white border border-violet-500/30 hover:from-violet-500/30 hover:to-cyan-500/30 transition-all"
          >
            Tekrar Oyna
          </button>
          <button
            onClick={onExit}
            className="flex-1 rounded-full bg-slate-800/50 px-6 py-3 text-sm font-medium text-slate-300 border border-slate-700/50 hover:bg-slate-800 transition-all"
          >
            Çıkış
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// Main Telepathy Mode Component
export const TelepathyMode = ({ onBack, onHome }) => {
  const [screen, setScreen] = useState('nickname');
  const [ws, setWs] = useState(null);
  const [nickname, setNickname] = useState('');
  const [userLocation, setUserLocation] = useState(null);
  const [partnerInfo, setPartnerInfo] = useState(null);
  const [currentRole, setCurrentRole] = useState(null);
  const [currentSymbol, setCurrentSymbol] = useState(null);
  const [currentRound, setCurrentRound] = useState(1);
  const [canGuess, setCanGuess] = useState(false);
  const [roundResult, setRoundResult] = useState(null);
  const [gameSummary, setGameSummary] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatOpen, setChatOpen] = useState(false);
  
  const wsRef = useRef(null);
  
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);
  
  const connectWebSocket = useCallback((name, location) => {
    const wsUrl = `${WS_URL}/api/telepathy/ws`;
    const socket = new WebSocket(wsUrl);
    
    socket.onopen = () => {
      socket.send(JSON.stringify({ 
        action: 'join', 
        nickname: name,
        location: location
      }));
    };
    
    socket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      switch (data.type) {
        case 'waiting':
          setScreen('waiting');
          break;
          
        case 'matched':
          setPartnerInfo(data.partner);
          setCurrentRole(data.your_role);
          if (data.your_role === 'sender') {
            setCurrentSymbol(data.current_symbol);
          }
          setScreen('globe');
          break;
          
        case 'game_start':
          setCurrentRound(data.round);
          setScreen('game');
          break;
          
        case 'thought_sent':
          setCanGuess(true);
          break;
          
        case 'round_result':
          setRoundResult(data);
          setCanGuess(false);
          setTimeout(() => setRoundResult(null), 2000);
          break;
          
        case 'new_round':
          setCurrentRound(data.round);
          setCurrentRole(data.your_role);
          setCurrentSymbol(data.current_symbol);
          setCanGuess(false);
          break;
          
        case 'game_over':
          setGameSummary(data);
          setScreen('summary');
          break;
          
        case 'chat':
          setChatMessages(prev => [...prev, data]);
          break;
          
        case 'partner_left':
          alert(data.message);
          if (wsRef.current) {
            wsRef.current.close();
          }
          onBack();
          break;
          
        default:
          break;
      }
    };
    
    socket.onclose = () => {
      setWs(null);
    };
    
    socket.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
    
    wsRef.current = socket;
    setWs(socket);
  }, [onBack]);
  
  const handleJoin = (name, location) => {
    setNickname(name);
    setUserLocation(location);
    connectWebSocket(name, location);
  };
  
  const handleCancel = () => {
    if (wsRef.current) {
      wsRef.current.send(JSON.stringify({ action: 'leave' }));
      wsRef.current.close();
    }
    setScreen('nickname');
  };
  
  const handleGlobeComplete = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action: 'ready' }));
    }
  };
  
  const handleSendThought = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action: 'send_thought' }));
    }
  };
  
  const handleGuess = (symbol) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action: 'guess', symbol }));
    }
  };
  
  const handleSendChat = (message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action: 'chat', message }));
    }
  };
  
  const handlePlayAgain = () => {
    setChatMessages([]);
    setGameSummary(null);
    setRoundResult(null);
    setCurrentRound(1);
    setCanGuess(false);
    setPartnerInfo(null);
    
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    connectWebSocket(nickname, userLocation);
  };
  
  const handleExit = () => {
    if (wsRef.current) {
      wsRef.current.send(JSON.stringify({ action: 'leave' }));
      wsRef.current.close();
    }
    onBack();
  };
  
  return (
    <div className="min-h-screen relative" data-testid="telepathy-mode">
      <AnimatePresence>
        {screen === 'globe' && (
          <Globe3D
            userLocation={userLocation}
            partnerLocation={partnerInfo?.location}
            onAnimationComplete={handleGlobeComplete}
          />
        )}
      </AnimatePresence>
      
      {screen === 'nickname' && (
        <NicknameEntry onSubmit={handleJoin} onBack={onBack} />
      )}
      
      {screen === 'waiting' && (
        <WaitingScreen location={userLocation} onCancel={handleCancel} />
      )}
      
      {screen === 'game' && (
        <div className="min-h-screen flex flex-col items-center justify-center p-4">
          <div className="fixed top-6 left-6 z-40 flex items-center gap-4">
            <button
              onClick={handleExit}
              className="glass-panel p-3 text-slate-400 hover:text-white transition-all"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="glass-panel px-4 py-2">
              <span className="text-slate-400 text-sm">Tur </span>
              <span className="text-cyan-400 font-medium">{currentRound}</span>
              <span className="text-slate-400 text-sm"> / 10</span>
            </div>
          </div>
          
          <div className="fixed top-6 right-6 z-40 flex items-center gap-3">
            <div className="glass-panel px-4 py-2 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-slate-300 text-sm">{partnerInfo?.nickname}</span>
              {partnerInfo?.location && (
                <span className="text-slate-500 text-xs">
                  ({partnerInfo.location.city})
                </span>
              )}
            </div>
            
            {/* Home Button */}
            <button
              onClick={onHome || onBack}
              className="glass-panel p-3 text-slate-400 hover:text-white transition-all flex items-center gap-2"
              data-testid="home-button"
            >
              <Home className="w-5 h-5" />
              <span className="text-sm">Home</span>
            </button>
          </div>
          
          <div className="max-w-md w-full">
            {currentRole === 'sender' ? (
              <SenderView
                symbol={currentSymbol}
                onSendThought={handleSendThought}
                partnerNickname={partnerInfo?.nickname}
              />
            ) : (
              <ReceiverView
                onGuess={handleGuess}
                canGuess={canGuess}
                partnerNickname={partnerInfo?.nickname}
              />
            )}
          </div>
          
          <AnimatePresence>
            {roundResult && <RoundResult result={roundResult} />}
          </AnimatePresence>
          
          <MiniChat
            messages={chatMessages}
            onSend={handleSendChat}
            isOpen={chatOpen}
            onToggle={() => setChatOpen(!chatOpen)}
          />
        </div>
      )}
      
      {screen === 'summary' && gameSummary && (
        <GameSummary
          summary={gameSummary}
          onPlayAgain={handlePlayAgain}
          onExit={handleExit}
        />
      )}
    </div>
  );
};

export default TelepathyMode;
